import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * `<TruncatedCell>` — a uniform "truncated text with full-content tooltip"
 * primitive for table cells. Used for any column where the value may
 * overflow (reasons, descriptions, subjects, comments).
 *
 * Usage:
 *   <TruncatedCell text={row.subject} maxWidth={220} testId={`subject-${row.id}`} />
 *
 * Props:
 *   - `text`        — string to render (required). Falsy values render `-`.
 *   - `maxWidth`    — max cell width in px (default 240). Don't pass `max-w-*`
 *                     classes; just give a number.
 *   - `placement`   — tooltip side, default `top`.
 *   - `tooltipMax`  — tooltip max-width Tailwind class, default `max-w-md`.
 *   - `testId`      — data-testid attribute for the trigger span.
 *   - `className`   — extra classes on the visible truncated span.
 *
 * Semantics:
 *   - No tooltip is mounted when `text` is empty — keeps DOM small.
 *   - Multi-line strings preserved in tooltip via `whitespace-pre-wrap`.
 *   - Trigger uses `cursor-help` so the affordance is visible.
 */
export function TruncatedCell({
  text,
  maxWidth = 240,
  placement = 'top',
  tooltipMax = 'max-w-md',
  testId,
  className = '',
}) {
  if (!text) return <span className="text-muted-foreground">-</span>;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`block truncate cursor-help ${className}`}
            style={{ maxWidth: `${maxWidth}px` }}
            data-testid={testId}
          >
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={placement}
          className={`${tooltipMax} bg-background text-foreground border border-border shadow-lg p-3 text-xs leading-relaxed whitespace-pre-wrap`}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default TruncatedCell;
